import { useState, useEffect, useRef } from 'react';
import Gateway from '../api/gateway';

/**
 * Хук для загрузки skybox (celestial bodies) звёздной системы.
 *
 * Использует universe_gateway (baseURL :4000).
 * REST: GET /api/skybox/full?system_id=X&lod=0
 *
 * Кэширует результат на клиенте (Map в памяти) с TTL 5 минут
 * (skybox меняется чаще, чем mesh структур — движение планет).
 *
 * @param {string} systemId - ID звёздной системы (characterMeta.system_id)
 * @param {object} [options]
 * @param {number} [options.lod=0] - уровень детализации: 0 = full, 1..3 = упрощённый
 * @param {number} [options.ttlMs=300000] - TTL кэша (5 минут)
 * @param {boolean} [options.enabled=true] - если false, запрос не выполняется
 *
 * @returns {{ skybox: object | null, loading: boolean, error: Error | null, reload: () => void }}
 */
export function useSkyboxMesh(systemId, options = {}) {
  const { lod = 0, ttlMs = 5 * 60 * 1000, enabled = true } = options;

  const [skybox, setSkybox] = useState(() => getCached(systemId, lod, ttlMs));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reloadCounter, setReloadCounter] = useState(0);

  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!enabled || !systemId) {
      setLoading(false);
      return;
    }

    const cached = getCached(systemId, lod, ttlMs);
    if (cached) {
      setSkybox(cached);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchSkybox(systemId, lod)
      .then((data) => {
        if (cancelled || !mountedRef.current) return;
        cacheSet(systemId, lod, data);
        setSkybox(data);
        setError(null);
      })
      .catch((err) => {
        if (cancelled || !mountedRef.current) return;
        // eslint-disable-next-line no-console
        console.error(`[useSkyboxMesh] system=${systemId} lod=${lod} error:`, err);
        setError(err instanceof Error ? err : new Error(String(err)));
        setSkybox(null);
      })
      .finally(() => {
        if (cancelled || !mountedRef.current) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [systemId, lod, enabled, ttlMs, reloadCounter]);

  const reload = () => {
    invalidateCache(systemId, lod);
    setReloadCounter((n) => n + 1);
  };

  return { skybox, loading, error, reload };
}

// ============================================================================
// INTERNAL — FETCH
// ============================================================================

async function fetchSkybox(systemId, lod) {
  const key = cacheKey(systemId, lod);

  if (INFLIGHT.has(key)) return INFLIGHT.get(key);

  const promise = (async () => {
    try {
      // 🔥 universe_gateway (baseURL :4000)
      const response = await Gateway.universeGateway.get('/api/skybox/full', {
        params: { system_id: systemId, lod },
      });
      const data = response?.data || response;
      const skybox = data?.skybox || data?.data || data;
      if (!skybox) {
        throw new Error(`Empty skybox for system=${systemId}`);
      }
      return skybox;
    } finally {
      INFLIGHT.delete(key);
    }
  })();

  INFLIGHT.set(key, promise);
  return promise;
}

// ============================================================================
// INTERNAL — CACHE
// ============================================================================

const CACHE = new Map();
const INFLIGHT = new Map();

function cacheKey(systemId, lod) {
  return `${systemId}:lod${lod}`;
}

function getCached(systemId, lod, ttlMs) {
  if (!systemId) return null;
  const key = cacheKey(systemId, lod);
  const entry = CACHE.get(key);
  if (!entry) return null;
  if (Date.now() - entry.timestamp > ttlMs) {
    CACHE.delete(key);
    return null;
  }
  return entry.value;
}

function cacheSet(systemId, lod, value) {
  CACHE.set(cacheKey(systemId, lod), { value, timestamp: Date.now() });
}

function invalidateCache(systemId, lod) {
  if (!systemId) return;
  CACHE.delete(cacheKey(systemId, lod));
  INFLIGHT.delete(cacheKey(systemId, lod));
}

// ============================================================================
// PUBLIC UTILS
// ============================================================================

export function clearSkyboxCache() {
  CACHE.clear();
  INFLIGHT.clear();
}

export function invalidateSkybox(systemId, lod = 0) {
  invalidateCache(systemId, lod);
}

export function skyboxCacheSize() {
  return CACHE.size;
}